/**
 * /changelog — Customer-facing release notes.
 *
 * Real changelog drawn from the project's git history. Each entry maps a date
 * + version + 1-3 customer-facing change bullets.
 *
 * Replaces the Phase 9 ComingSoonShell stub per Phase 3 Task 3.7 of the
 * 2026-05-16 implementation plan.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { MarketingLayout } from '../components/layouts/marketing-layout';

export const Route = createFileRoute('/changelog')({
  component: ChangelogPage,
});

interface Entry {
  date: string;
  version: string;
  title: string;
  bullets: string[];
}

const ENTRIES: Entry[] = [
  {
    date: '17 May 2026',
    version: 'v0.10.0',
    title: 'Marketing surface refresh',
    bullets: [
      'Product, security, customers, firms, and compliance pages rewritten with real content.',
      'Public documentation hub launched at /docs with onboarding, signing, and DSAR guides.',
      'Brandbook published at /brandbook for partners and integrators.',
    ],
  },
  {
    date: '16 May 2026',
    version: 'v0.9.0',
    title: 'Stripe billing wired, Anna fixture retired',
    bullets: [
      '/admin/billing wired to Stripe: no more hard-coded demo numbers; activates once Stripe keys are configured.',
      'Beneficiary dashboard hydrates from real backend, ending the Anna fixture pattern.',
      'External signing flow wired end-to-end: the highest-stakes lifecycle path.',
    ],
  },
  {
    date: '15 May 2026',
    version: 'v0.8.0',
    title: 'Per-trust drill-down endpoints',
    bullets: [
      'Beneficiaries, ownership, tax, LoA-LoW, and resolutions endpoints live per trust.',
      'Audit, documents, meetings, and risk-rating endpoints live per trust.',
      'Dashboard endpoints close the Anna-fixture leak across cross-trust views.',
    ],
  },
  {
    date: '14 May 2026',
    version: 'v0.7.0',
    title: 'Billing, inbox, and external-signer rails',
    bullets: [
      'Stripe-backed billing endpoints: invoice history and subscription management.',
      'Inbox endpoints with Cosmos containers backing notification history.',
      'External recipient signing flow: single-use link with one-time-code verification.',
    ],
  },
  {
    date: '13 May 2026',
    version: 'v0.6.0',
    title: 'Audit completeness pass',
    bullets: [
      'Sixteen handlers and the audit-changefeed function gain dedicated test coverage.',
      'Financial Intelligence Centre goAML XML push built: direct eFiling activates once the firm’s goAML credentials are configured.',
      'Rate-limit status responses now report from Cosmos-backed counters.',
    ],
  },
  {
    date: '12 May 2026',
    version: 'v0.5.0',
    title: 'Security hardening (Phase 1 milestone)',
    bullets: [
      'Every API request now carries per-operation authentication end to end.',
      'Duplicate-submission protection added across write actions.',
      'Cross-instance replay protection added for webhook deliveries.',
    ],
  },
  {
    date: '11 May 2026',
    version: 'v0.4.0',
    title: 'Multi-document consistency and endpoint hardening',
    bullets: [
      'Admin IO designation and trustee appointment paths now serialise correctly.',
      'Production API endpoints now require a verified sign-in on every request.',
      'Body-size and content-type guards added to request parsing.',
    ],
  },
  {
    date: '8 May 2026',
    version: 'v0.1.0',
    title: 'Documentation reconciliation',
    bullets: [
      'Amazon SES to Azure Communication Services migration reflected across all four READMEs.',
      'Four placeholder policies promoted to draft status with real content.',
      'Resolution body-swap defence requirement created for the fiduciary engine.',
    ],
  },
  {
    date: '7 May 2026',
    version: 'v0.0.9',
    title: 'Test coverage and decision log',
    bullets: [
      'Coverage thresholds enforced in vitest for all three codebases (backend, frontend, mobile).',
      'Decision log gap L17-L23 closed with renumbering note and architectural captures.',
      'Mobile test runner includes screens and components in vitest paths.',
    ],
  },
  {
    date: '6 May 2026',
    version: 'v0.0.8',
    title: 'Azure deployment infrastructure',
    bullets: [
      'Bicep guards added for Phase 05 and 06 production backfill (autoscale plus locked WORM).',
      'Eleven missing handler imports wired into the API Function App entry point.',
      'Always Encrypted policy deferment on existing containers documented for next migration.',
    ],
  },
];

function ChangelogPage() {
  useEffect(() => {
    document.title = 'Changelog · Elreign: what shipped, when';
    const meta = document.querySelector('meta[name="description"]') ?? (() => {
      const m = document.createElement('meta');
      m.name = 'description';
      document.head.appendChild(m);
      return m;
    })();
    meta.setAttribute(
      'content',
      'Elreign release notes. Every change customers see, with the date it shipped and the context it shipped in.',
    );
  }, []);

  return (
    <MarketingLayout>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '72px 24px 96px' }}>
        {/* Hero */}
        <header style={{ marginBottom: 56 }}>
          <p className="eyebrow" style={{ marginBottom: 18 }}>Changelog</p>
          <h1
            className="serif"
            style={{
              fontSize: 'clamp(40px, 6vw, 56px)',
              fontStyle: 'italic',
              fontWeight: 300,
              color: 'var(--ink)',
              lineHeight: 1.1,
              letterSpacing: '-0.025em',
              margin: '0 0 24px',
            }}
          >
            What shipped, <em>when.</em>
          </h1>
          <p style={{ fontSize: 19, color: 'var(--ink-3)', lineHeight: 1.65, margin: 0, maxWidth: 600 }}>
            Customer-facing changes only. Internal refactors, dependency bumps, and CI tweaks are
            kept in our engineering log. Material changes that affect tenants are announced by
            email to designated firm contacts at least 72 hours before they ship.
          </p>
        </header>

        {/* Entries */}
        <section style={{ display: 'flex', flexDirection: 'column' }}>
          {ENTRIES.map((entry, i) => (
            <article
              key={entry.version}
              style={{
                paddingTop: 32,
                paddingBottom: 32,
                borderTop: i === 0 ? '1px solid var(--hairline)' : 'none',
                borderBottom: '1px solid var(--hairline)',
                display: 'grid',
                gridTemplateColumns: 'minmax(140px, 180px) 1fr',
                gap: 24,
              }}
            >
              <div>
                <div
                  className="mono"
                  style={{
                    fontSize: 12.5,
                    color: 'var(--accent-600)',
                    letterSpacing: '0.02em',
                    marginBottom: 6,
                  }}
                >
                  {entry.version}
                </div>
                <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{entry.date}</div>
              </div>
              <div>
                <h3
                  style={{
                    fontSize: 19,
                    fontWeight: 600,
                    color: 'var(--ink)',
                    margin: '0 0 12px',
                    letterSpacing: '-0.01em',
                    lineHeight: 1.3,
                  }}
                >
                  {entry.title}
                </h3>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                  {entry.bullets.map((b) => (
                    <li
                      key={b}
                      style={{
                        fontSize: 15,
                        color: 'var(--ink-2)',
                        lineHeight: 1.65,
                        paddingLeft: 16,
                        position: 'relative',
                        marginBottom: 6,
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: '0.55em',
                          width: 5,
                          height: 5,
                          borderRadius: '50%',
                          background: 'var(--accent-400)',
                        }}
                      />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </section>

        {/* Footer note */}
        <p style={{ marginTop: 40, fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.6 }}>
          Subscribe to release notifications by email: write to{' '}
          <a href="mailto:changelog@elreign.io" style={{ color: 'var(--primary-600)' }}>
            changelog@elreign.io
          </a>
          .
        </p>
      </div>
    </MarketingLayout>
  );
}
